// Home's server half, as the screens read it (lane B10 S16). B1's `home.open` through the generated client (`homeOpen`,
// its `WidgetOut`; the preview rows are B1's `ActionRow`). The widget data shapes are S13's, frozen for the parallel
// launch (PARALLEL-MAP §2.1 "S13 → S16 widget data"): every route an app route, no key named score or rank on a person
// anywhere. The readers below never trust the server's dict blindly: a missing list is empty, a missing number is null.

import { homeOpen, type ActionRow, type HomeOpenOutput, type WidgetOut } from "@/api/tools";
import type { SeatKey } from "@/mc/labels";

// ---- B1 home.open --------------------------------------------------------------------------------------------------

export { homeOpen };
export type { HomeOpenOutput };
export type HomeWidgetOut = WidgetOut;
export const HOME_QUERY_KEY = ["home.open"] as const;

/** B1's slots (app/dashboard.py WIDGET_SLOTS, DS row 65's order). */
export const WIDGET_SLOTS = ["alert", "kpis", "action_center", "sections", "health_readiness", "timelines", "reports"] as const;

/** B1's Action Center row, as the preview widgets carry it (the History columns are absent there). */
export type ActionRowOut = Pick<
  ActionRow,
  "id" | "category" | "tier" | "tier_word" | "verb" | "title" | "route" | "requisition_id" | "requisition_title" | "due_at" | "opened_at"
>;

// ---- S13's widget data (frozen) ------------------------------------------------------------------------------------

export type KpiKey = "open_requisitions" | "active_candidates" | "time_to_acceptance" | "offers_accepted" | "req_health";
export type Light = "green" | "yellow" | "red";

export interface BandCounts {
  green: number;
  yellow: number;
  red: number;
}

export interface KpiItem {
  key: KpiKey | string;
  label_key: string;
  value: number | null;
  unit: "count" | "days" | "percent" | string;
  status: "ok" | "not_yet_available" | string;
  detail?: { time_to_fill?: number | null; within_target_pct?: number | null; bands?: BandCounts } | undefined;
  route: string;
}

export interface KpisData {
  period: { start: string; end: string } | null;
  items: KpiItem[];
}

export interface LinkedKey {
  key: string;
  label_key: string;
  route: string;
}

export interface ReqHealthRow {
  requisition_id: string;
  title: string;
  score: number | null;
  light: Light | null;
  drivers: LinkedKey[];
  route: string;
}

export interface ReqHealthData {
  mean: number | null;
  bands: BandCounts;
  rows: ReqHealthRow[];
}

export interface ReadinessRow {
  requisition_id: string;
  title: string;
  percent: number | null;
  missing: LinkedKey[];
  posting_blocked: false;
  route: string;
}

export interface SearchReadinessData {
  rows: ReadinessRow[];
}

export interface RequisitionLink {
  requisition_id: string;
  title: string;
  route: string;
}

export interface TimelinesData {
  rows: RequisitionLink[];
}

export interface ReportsData {
  families: LinkedKey[];
}

export interface ActiveRequisitionRow {
  requisition_id: string;
  title: string;
  status: string | null;
  health_score: number | null;
  health_light: Light | null;
  readiness_percent: number | null;
  /** DC-31 column (applications.COLUMNS) → count. */
  stage_counts: Record<string, number>;
  route: string;
}

export interface ActiveRequisitionsData {
  scope_words: string[];
  rows: ActiveRequisitionRow[];
}

export interface ExecutiveLinkData {
  route: string;
}

export interface DispositionRow {
  requisition_id: string;
  title: string;
  open: number;
  route: string;
}

export interface DispositionWorklistData {
  open: number;
  rows: DispositionRow[];
}

/** The Coordinator's Ready for HRIS (S14b, over B9's handoff): per requisition, the hires still to confirm in HRIS. */
export interface HrisHandoffRow {
  requisition_id: string;
  title: string;
  ready: number;
  route: string;
}

export interface HrisHandoffData {
  ready: number;
  rows: HrisHandoffRow[];
}

// ---- figures -------------------------------------------------------------------------------------------------------

/** A figure that has no value (a producer not merged, nothing measured): never 0 (PLAN D2). */
export const DASH = "—";

/** A figure as drawn: whole counts, days to one place, a percent with its sign; "—" when there is none. */
export function figure(value: number | null | undefined, unit: string): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return DASH;
  if (unit === "percent") return `${Math.round(value)}%`;
  if (unit === "days") return String(Math.round(value * 10) / 10);
  return String(Math.round(value));
}

// ---- readers -------------------------------------------------------------------------------------------------------

type Loose = Record<string, unknown>;

const obj = (value: unknown): Loose => (value && typeof value === "object" && !Array.isArray(value) ? (value as Loose) : {});
const list = (value: unknown): Loose[] => (Array.isArray(value) ? value.map(obj) : []);
const str = (value: unknown): string => (typeof value === "string" ? value : "");
const num = (value: unknown): number | null => (typeof value === "number" && Number.isFinite(value) ? value : null);
const count = (value: unknown): number => num(value) ?? 0;
const light = (value: unknown): Light | null => (value === "green" || value === "yellow" || value === "red" ? value : null);
const words = (value: unknown): string[] => (Array.isArray(value) ? value.filter((w): w is string => typeof w === "string") : []);

/** An app route the hash router serves ("#…"); anything else is no link. */
export const appRoute = (value: unknown): string => {
  const route = str(value);
  return route.startsWith("#") ? route : "";
};

const bands = (value: unknown): BandCounts => {
  const b = obj(value);
  return { green: count(b.green), yellow: count(b.yellow), red: count(b.red) };
};

const linked = (value: unknown): LinkedKey[] =>
  list(value).map((row) => ({ key: str(row.key), label_key: str(row.label_key), route: appRoute(row.route) })).filter((row) => row.key);

const reqLink = (row: Loose): RequisitionLink => ({ requisition_id: str(row.requisition_id), title: str(row.title), route: appRoute(row.route) });

export function kpisOf(data: Loose): KpisData {
  const period = obj(data.period);
  return {
    period: str(period.start) && str(period.end) ? { start: str(period.start), end: str(period.end) } : null,
    items: list(data.items).map((item) => {
      const detail = obj(item.detail);
      return {
        key: str(item.key),
        label_key: str(item.label_key),
        value: num(item.value),
        unit: str(item.unit) || "count",
        status: str(item.status) || "ok",
        detail: {
          time_to_fill: num(detail.time_to_fill),
          within_target_pct: num(detail.within_target_pct),
          ...(detail.bands ? { bands: bands(detail.bands) } : {}),
        },
        route: appRoute(item.route),
      };
    }).filter((item) => item.key),
  };
}

export function reqHealthOf(data: Loose): ReqHealthData {
  return {
    mean: num(data.mean),
    bands: bands(data.bands),
    rows: list(data.rows).map((row) => ({
      ...reqLink(row), score: num(row.score), light: light(row.light), drivers: linked(row.drivers),
    })).filter((row) => row.requisition_id),
  };
}

export function searchReadinessOf(data: Loose): SearchReadinessData {
  return {
    rows: list(data.rows).map((row) => ({
      ...reqLink(row), percent: num(row.percent), missing: linked(row.missing), posting_blocked: false as const,
    })).filter((row) => row.requisition_id),
  };
}

export function timelinesOf(data: Loose): TimelinesData {
  return { rows: list(data.rows).map(reqLink).filter((row) => row.requisition_id) };
}

export function reportsOf(data: Loose): ReportsData {
  return { families: linked(data.families) };
}

export function activeRequisitionsOf(data: Loose): ActiveRequisitionsData {
  return {
    scope_words: words(data.scope_words),
    rows: list(data.rows).map((row) => {
      const stages = obj(row.stage_counts);
      return {
        ...reqLink(row),
        status: str(row.status) || null,
        health_score: num(row.health_score),
        health_light: light(row.health_light),
        readiness_percent: num(row.readiness_percent),
        stage_counts: Object.fromEntries(Object.entries(stages).map(([column, n]) => [column, count(n)])),
      };
    }).filter((row) => row.requisition_id),
  };
}

export function executiveLinkOf(data: Loose): ExecutiveLinkData {
  return { route: appRoute(data.route) };
}

export function dispositionWorklistOf(data: Loose): DispositionWorklistData {
  const rows = list(data.rows).map((row) => ({ ...reqLink(row), open: count(row.open) })).filter((row) => row.requisition_id);
  return { open: num(data.open) ?? rows.reduce((sum, row) => sum + row.open, 0), rows };
}

export function hrisHandoffOf(data: Loose): HrisHandoffData {
  const rows = list(data.rows).map((row) => ({ ...reqLink(row), ready: count(row.ready) })).filter((row) => row.requisition_id);
  return { ready: num(data.ready) ?? rows.reduce((sum, row) => sum + row.ready, 0), rows };
}

export function actionRowsOf(data: Loose): ActionRowOut[] {
  return list(data.rows).map((row) => ({
    id: str(row.id),
    category: str(row.category),
    tier: str(row.tier) || "due",
    tier_word: str(row.tier_word) || null,
    verb: str(row.verb) || null,
    title: str(row.title),
    route: appRoute(row.route) || null,
    requisition_id: str(row.requisition_id) || null,
    requisition_title: str(row.requisition_title) || null,
    due_at: str(row.due_at) || null,
    opened_at: str(row.opened_at),
  })).filter((row) => row.id);
}

// ---- the seats a widget is for (data-roles; descriptive only — the server composes and trims) ----------------------

const OFFICE: readonly SeatKey[] = ["hm", "recruiter", "coordinator", "hrbp", "compensation", "taManager", "admin"];
/** PLAN D9's general dashboard: "TA/HRBP/selected HR get general dashboard." */
const GENERAL: readonly SeatKey[] = ["hrbp", "compensation", "taManager", "admin"];

/** Per widget key, the seats PLAN D9 (and B1's WidgetDef roles) give it. */
export const WIDGET_SEATS: Readonly<Record<string, readonly SeatKey[]>> = {
  breach_alert: [...OFFICE, "interviewer"],
  kpis: [...GENERAL, "recruiter"],
  action_center_preview: [...OFFICE, "interviewer"],
  own_open_reqs: ["hm"],
  assigned_interviewer_work: ["hm"],
  assigned_list: ["interviewer"],
  active_requisitions: [...GENERAL, "recruiter", "coordinator"],
  disposition_worklist: ["recruiter"],
  hris_handoff: ["coordinator"],
  req_health: OFFICE,
  search_readiness: OFFICE,
  timelines: [...GENERAL, "recruiter", "hm"],
  reports: OFFICE,
  executive_link: ["taManager", "admin"],
};

export const seatsOf = (key: string): readonly SeatKey[] => WIDGET_SEATS[key] ?? OFFICE;
